<template>
  <div
    :class="[$style.base, { [$style.smallScreen]: $screen === 's', [$style.wide]: wide, [$style.scrolled]: isScrolled }]"
  >
    <BaseSpinner v-if="loading" />
    <component
      v-else
      :is="useForm ? 'BaseForm' : 'div'"
      :class="$style.formWrap"
      :fullHeight="useForm"
      @submit="$emit('submit')"
    >
      <div
        ref="scrollContainer"
        :class="$style.content"
        v-test="'admin-page-content'"
      >
        <div
          :class="
          $style.contentInner"
        >
          <div :class="$style.header">
            <BaseHeading
              v-if="heading"
              size="l"
            >
              {{ heading }}
            </BaseHeading>
            <div :class="$style.actions">
              <slot name="actions" />
            </div>
          </div>
          <div>
            <slot />
          </div>
        </div>
      </div>
    </component>
    <router-view @updated="$emit('updated', $event)" />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useScroll } from '@vueuse/core';

defineProps<{
  heading?: string;
  wide?: boolean;
  useForm?: boolean;
  loading?: boolean;
}>();

defineEmits(['submit', 'updated']);

const scrollContainer = useTemplateRef('scrollContainer');
const { y } = useScroll(scrollContainer);

const isScrolled = computed(() => y.value > 0);
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
}

.formWrap {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
}

.contentInner {
  padding: 0 $spacing $spacing $spacing;
  
  .base:not(.smallScreen) & {
    padding-bottom: 80px; // Leave space on the bottom for Intercom button
  }

  .base.smallScreen & {
    padding-bottom: $input-height + ($spacing * 3);  // Leave space on the bottom for the fixed footer
  }
}

.contentInner {
  margin: 0 auto;

  .base:not(.wide) & {
    max-width: 800px;
  }

  .base.wide & {
    max-width: 1200px;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: $input-height;
  flex-wrap: wrap;
  gap: $spacing;
  padding: $spacing 0;

  .base:not(.smallScreen) & {
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    z-index: 3;
    background-color: var(--color-bg-secondary);
  }

  &:before {
    .base:not(.smallScreen) & {
      content: "";
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: $spacing * 1.5;
      background: linear-gradient(to bottom, var(--color-bg-secondary), transparent);
      pointer-events: none;
      transition: opacity 0.2s ease;
    }

    .base:not(.smallScreen):not(.scrolled) & {
      opacity: 0;
    }
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: $spacing;

  &:empty {
    display: none;
  }

  .base.smallScreen & {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 1000;
    background-color: var(--color-bg);
    padding: $spacing;
    border-top: 1px solid var(--color-border);
  }
}
</style>
